import { appBackendApiClient } from './apiClient';
import {
  AdminCircle,
  AdminCircleCreatePayload,
  AdminCircleDetail,
  AdminCircleFilters,
  AdminCircleActivity,
  AdminCircleActivityComment,
  AdminCircleLeaderboardEntry,
  AdminCircleMember,
  AdminCircleUpdatePayload,
} from '../types';

const toNumber = (value: unknown) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapMember = (raw: any): AdminCircleMember => ({
  userId: raw.userId,
  displayName: raw.displayName || 'Circle member',
  avatarUrl: raw.avatarUrl ?? null,
  role: raw.role,
  status: raw.status,
  joinedAt: raw.joinedAt,
  totalContributedPoints: toNumber(raw.totalContributedPoints),
  totalContributedAmount: toNumber(raw.totalContributedAmount),
});

const mapLeaderboardEntry = (raw: any): AdminCircleLeaderboardEntry => ({
  userId: raw.userId ?? null,
  displayName: raw.isAnonymous ? null : raw.displayName || 'Circle member',
  avatarUrl: raw.avatarUrl ?? null,
  role: raw.role ?? null,
  totalContributedPoints: toNumber(raw.totalContributedPoints),
  totalContributedAmount: toNumber(raw.totalContributedAmount),
  rank: toNumber(raw.rank),
  isAnonymous: Boolean(raw.isAnonymous),
});

const mapActivityComment = (raw: any): AdminCircleActivityComment => ({
  id: raw.id,
  body: raw.body,
  author: {
    userId: raw.author?.userId,
    displayName: raw.author?.displayName || 'Circle member',
    avatarUrl: raw.author?.avatarUrl ?? null,
  },
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
});

const mapActivity = (raw: any): AdminCircleActivity => ({
  id: raw.id,
  circleId: raw.circleId,
  type: raw.type,
  message: raw.message,
  author: {
    userId: raw.author?.userId,
    displayName: raw.author?.displayName || 'Circle member',
    avatarUrl: raw.author?.avatarUrl ?? null,
  },
  reactionCount: toNumber(raw.reactionCount),
  commentCount: toNumber(raw.commentCount),
  viewerHasReacted: Boolean(raw.viewerHasReacted),
  comments: Array.isArray(raw.comments) ? raw.comments.map(mapActivityComment) : [],
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
});

const mapCircle = (raw: any): AdminCircle => ({
  id: raw.id,
  name: raw.name,
  description: raw.description,
  coverPhotoUrl: raw.coverPhotoUrl ?? null,
  coverPhotoStorageKey: raw.coverPhotoStorageKey ?? null,
  isPublic: Boolean(raw.isPublic ?? true),
  isFeatured: Boolean(raw.isFeatured),
  creator: {
    userId: raw.creator?.userId,
    displayName: raw.creator?.displayName || 'Circle creator',
    avatarUrl: raw.creator?.avatarUrl ?? null,
  },
  creatorEmail: raw.creatorEmail ?? undefined,
  memberCount: toNumber(raw.memberCount),
  totalAmountRaised: toNumber(raw.totalAmountRaised),
  totalPoints: toNumber(raw.totalPoints),
  isDefault: Boolean(raw.isDefault),
  currentUserRole: raw.currentUserRole ?? null,
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
  deletedAt: raw.deletedAt ?? null,
});

const mapCircleDetail = (raw: any): AdminCircleDetail => ({
  ...mapCircle(raw),
  members: Array.isArray(raw.members) ? raw.members.map(mapMember) : [],
  leaderboard: Array.isArray(raw.leaderboard)
    ? raw.leaderboard.map(mapLeaderboardEntry)
    : [],
  activities: Array.isArray(raw.activities) ? raw.activities.map(mapActivity) : [],
  anonymousContribution: raw.anonymousContribution
    ? {
        totalContributedPoints: toNumber(raw.anonymousContribution.totalContributedPoints),
        totalContributedAmount: toNumber(raw.anonymousContribution.totalContributedAmount),
      }
    : null,
});

const buildQuery = (filters: AdminCircleFilters = {}) => {
  const query: Record<string, string | number | boolean> = {};

  if (filters.search?.trim()) {
    query.search = filters.search.trim();
  }
  if (filters.creator?.trim()) {
    query.creator = filters.creator.trim();
  }
  if (filters.memberCountMin !== '' && filters.memberCountMin !== undefined) {
    query.memberCountMin = filters.memberCountMin;
  }
  if (filters.memberCountMax !== '' && filters.memberCountMax !== undefined) {
    query.memberCountMax = filters.memberCountMax;
  }
  if (filters.createdFrom) {
    query.createdFrom = filters.createdFrom;
  }
  if (filters.createdTo) {
    query.createdTo = filters.createdTo;
  }
  if (filters.includeDeleted) {
    query.includeDeleted = true;
  }
  if (filters.isPublic !== undefined) {
    query.isPublic = filters.isPublic;
  }
  if (filters.isFeatured !== undefined) {
    query.isFeatured = filters.isFeatured;
  }

  return query;
};

interface CircleCoverUploadPayload {
  dataUrl: string;
  fileName?: string;
}

interface CircleCoverUploadResult {
  url: string;
  storageKey: string;
  contentType: string;
  size: number;
}

export const CircleService = {
  async getCircles(filters: AdminCircleFilters = {}): Promise<AdminCircle[]> {
    const response = await appBackendApiClient.get<any[]>('/admin/circles', {
      params: buildQuery(filters),
    });
    return response.data.map(mapCircle);
  },

  async getCircleById(circleId: string): Promise<AdminCircleDetail> {
    const response = await appBackendApiClient.get<any>(`/admin/circles/${circleId}`);
    return mapCircleDetail(response.data);
  },

  async updateCircle(
    circleId: string,
    payload: AdminCircleUpdatePayload,
  ): Promise<AdminCircleDetail> {
    const response = await appBackendApiClient.patch<any>(
      `/admin/circles/${circleId}`,
      payload,
    );
    return mapCircleDetail(response.data);
  },

  async createCircle(payload: AdminCircleCreatePayload): Promise<AdminCircleDetail> {
    const response = await appBackendApiClient.post<any>('/admin/circles', payload);
    return mapCircleDetail(response.data);
  },

  async uploadCoverPhoto(
    payload: CircleCoverUploadPayload,
  ): Promise<CircleCoverUploadResult> {
    const response = await appBackendApiClient.post<any>(
      '/circles/cover-photo-upload',
      payload,
    );

    return {
      url: response.data.url,
      storageKey: response.data.storageKey,
      contentType: response.data.contentType,
      size: toNumber(response.data.size),
    };
  },

  async deleteCircle(circleId: string): Promise<void> {
    await appBackendApiClient.delete(`/admin/circles/${circleId}`);
  },

  async adjustPoints(
    circleId: string,
    input: { points: number; reason: string },
  ): Promise<AdminCircleDetail> {
    const response = await appBackendApiClient.post<any>(
      `/admin/circles/${circleId}/points/adjustments`,
      input,
    );
    return mapCircleDetail(response.data);
  },

  async updateActivity(
    circleId: string,
    activityId: string,
    message: string,
  ): Promise<AdminCircleActivity> {
    const response = await appBackendApiClient.patch<any>(
      `/admin/circles/${circleId}/activities/${activityId}`,
      { message },
    );
    return mapActivity(response.data);
  },

  async deleteActivity(circleId: string, activityId: string): Promise<void> {
    await appBackendApiClient.delete(
      `/admin/circles/${circleId}/activities/${activityId}`,
    );
  },

  async updateActivityComment(
    circleId: string,
    activityId: string,
    commentId: string,
    body: string,
  ): Promise<AdminCircleActivity> {
    const response = await appBackendApiClient.patch<any>(
      `/admin/circles/${circleId}/activities/${activityId}/comments/${commentId}`,
      { body },
    );
    return mapActivity(response.data);
  },

  async deleteActivityComment(
    circleId: string,
    activityId: string,
    commentId: string,
  ): Promise<void> {
    await appBackendApiClient.delete(
      `/admin/circles/${circleId}/activities/${activityId}/comments/${commentId}`,
    );
  },
};
